import React from 'react'
import { Link } from 'react-router-dom'
import { Avatar, ListItemAvatar, ListItemText, Tooltip, IconButton } from '@mui/material'
import { Info } from '@mui/icons-material'
import { kebabCase } from 'lodash'
import ListItemBase from './ListItemBase'
import { ListItemCardProps } from './types'

/**
 * List Item Card component.
 *
 * @link https://mui.com/api/list-item/#main-content
 */
const ListItemCard: React.FC<ListItemCardProps> = ({
  id,
  icon,
  title,
  fieldName,
  tooltip,
  tooltipComponent,
  iconButton,
  children,
  isActive,
  path,
  type = 'basic',
  onClick,
  onChange,
  suppressFilePicker,
  isBordered = true,
  disabled = false,
  accept = ''
}) => {
  const fileProps = type === 'file' ? { component: 'label', htmlFor: `upload-file-${id}` } : {}
  fieldName = kebabCase(fieldName)
  return type === 'basic' && path ? (
    <Link to={path}>
      <ListItemBase
        data-testid={title}
        disabled={disabled}
        onClick={onClick}
        selected={isActive}
        sx={{
          border: isBordered ? `1px solid rgba(0, 0, 0, 0.12)` : 'transparent'
        }}
      >
        <ListItemAvatar>
          <Avatar sx={{ background: 'none' }}>{icon}</Avatar>
        </ListItemAvatar>
        <ListItemText primary={title} secondary={children} />
        {iconButton}
        {tooltip && (
          <Tooltip placement="top" title={tooltip} enterTouchDelay={0} leaveTouchDelay={5000}>
            <IconButton>
              <Info />
            </IconButton>
          </Tooltip>
        )}
      </ListItemBase>
    </Link>
  ) : (
    <ListItemBase
      data-testid={title}
      disabled={disabled}
      onClick={onClick}
      selected={isActive}
      data-element={`${fieldName}-file-button`}
      sx={{
        border: isBordered ? `1px solid rgba(0, 0, 0, 0.12)` : 'transparent'
      }}
      {...fileProps}
    >
      <ListItemAvatar data-element={`${fieldName}-avatar`}>
        <Avatar sx={{ background: 'none' }}>{icon}</Avatar>
      </ListItemAvatar>
      <ListItemText primary={<span data-element={`${fieldName}-primary`}>{title}</span>} secondary={children} />
      {iconButton}
      {tooltip && !tooltipComponent && !iconButton && (
        <Tooltip placement="top" title={tooltip} enterTouchDelay={0} leaveTouchDelay={5000}>
          <IconButton>
            <Info />
          </IconButton>
        </Tooltip>
      )}
      {!iconButton && tooltipComponent}
      {!suppressFilePicker && (
        <input
          hidden
          id={`upload-file-${id}`}
          data-element={`${fieldName}-file-input`}
          onChange={onChange}
          type="file"
          accept={accept}
        />
      )}
    </ListItemBase>
  )
}

export default ListItemCard
